import React, { useState, useEffect } from 'react'
import { Modal, Button, Spin, message, Checkbox, Space, Divider } from 'antd'
import { FilePdfOutlined, PrinterOutlined, EyeOutlined, SaveOutlined, FileTextOutlined } from '@ant-design/icons'
import { pdf, Document, Page, Text, View, StyleSheet, Font, Image } from '@react-pdf/renderer'
import { useStateContext } from '../../../../../../contexts/ContextProvider';

// Înregistrăm fonturile care suportă diacritice
Font.register({
  family: 'Roboto',
  fonts: [
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf',
      fontWeight: 300,
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf',
      fontWeight: 400,
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf',
      fontWeight: 500,
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf',
      fontWeight: 700,
    },
  ],
})

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 20,
    fontFamily: 'Roboto',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 30,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#cccccc',
  },
  companyInfo: {
    flex: 1,
    maxWidth: '45%',
  },
  documentTitle: {
    flex: 1,
    alignItems: 'flex-end',
    maxWidth: '45%',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333333',
    textAlign: 'right',
    marginBottom: 10,
    fontFamily: 'Roboto',
  },
  subtitle: {
    fontSize: 10,
    color: '#666666',
    textAlign: 'right',
    marginBottom: 5,
    fontFamily: 'Roboto',
  },
  companyName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: 5,
    fontFamily: 'Roboto',
  },
  companyDetails: {
    fontSize: 10,
    color: '#666666',
    marginBottom: 2,
    fontFamily: 'Roboto',
  },
  section: {
    marginVertical: 15,
    borderTop: '1px solid #eee',
    borderBottom: '1px solid #eee',
    paddingVertical: 10,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: 10,
    fontFamily: 'Roboto',
  },
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  column: {
    width: '50%',
    marginBottom: 5,
  },
  label: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#333333',
    fontFamily: 'Roboto',
  },
  value: {
    fontSize: 10,
    color: '#666666',
    fontFamily: 'Roboto',
  },
  table: {
    display: 'flex',
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#bfbfbf',
    borderRightWidth: 0,
    borderBottomWidth: 0,
    marginVertical: 10,
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableHeader: {
    backgroundColor: '#f0f0f0',
    fontWeight: 'bold',
  },
  tableCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#bfbfbf',
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 3,
    fontSize: 8,
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Roboto',
  },
  productCell: {
    textAlign: 'left',
    alignItems: 'flex-start',
  },
  productImage: {
    width: 50,
    height: 50,
    objectFit: 'contain',
    marginRight: 6,
  },
  productInfo: {
    flex: 1,
    flexDirection: 'column',
  },
  productRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  summary: {
    width: 250,
    alignSelf: 'flex-end',
    marginVertical: 15,
    borderTop: '1px solid #eee',
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottom: '1px solid #eee',
    paddingVertical: 8,
  },
  summaryTotal: {
    fontWeight: 'bold',
    fontSize: 12,
  },
  requestSection: {
    marginBottom: 20,
    borderBottom: '2px solid #ccc',
    paddingBottom: 15,
  },
  requestTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1890ff',
    marginBottom: 10,
    fontFamily: 'Roboto',
  },
  statusTag: {
    fontSize: 8,
    padding: 3,
    borderRadius: 3,
    marginRight: 5,
    marginBottom: 3,
  },
  footer: {
    fontSize: 8,
    color: '#777',
    textAlign: 'center',
    marginTop: 10,
    borderTop: '1px solid #eee',
    paddingTop: 5,
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
  },
  productName: {
    fontSize: 9,
    fontWeight: 'bold',
    marginBottom: 2,
    lineHeight: 1.2,
    textAlign: 'left',
    fontFamily: 'Roboto',
  },
  productSku: {
    fontSize: 7,
    color: '#666666',
    textAlign: 'left',
    fontFamily: 'Roboto',
  },
})

// Funcție pentru procesarea URL-urilor imaginilor
const processImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  
  if (imageUrl.startsWith('http')) {
    return imageUrl;
  }
  
  return `${process.env.REACT_APP_API_URL}${imageUrl}`;
};

// Funcție pentru a converti imaginile WebP în format suportat de PDF
const convertWebPToSupportedFormat = async (imageUrl) => {
  if (!imageUrl) return null;
  
  try {
    // Verificăm dacă imaginea este în format WebP
    if (imageUrl.toLowerCase().includes('.webp') || imageUrl.toLowerCase().includes('webp')) {
      console.log('Se convertește imaginea WebP:', imageUrl);
      
      // Creăm un canvas pentru a converti imaginea
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Încărcăm imaginea
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      return new Promise((resolve, reject) => {
        img.onload = () => {
          try {
            // Setăm dimensiunile canvas-ului
            canvas.width = img.width;
            canvas.height = img.height;
            
            // Desenăm imaginea pe canvas
            ctx.drawImage(img, 0, 0);
            
            // Convertim în PNG (format suportat de PDF)
            const pngDataUrl = canvas.toDataURL('image/png');
            console.log('Imaginea WebP a fost convertită cu succes în PNG');
            resolve(pngDataUrl);
          } catch (canvasError) {
            console.error('Eroare la desenarea imaginii pe canvas:', canvasError);
            resolve(imageUrl); // Returnăm URL-ul original în caz de eroare
          }
        };
        
        img.onerror = (error) => {
          console.warn('Nu s-a putut încărca imaginea WebP:', imageUrl, error);
          resolve(null);
        };
        
        img.src = imageUrl;
      });
    } else {
      // Dacă nu este WebP, returnăm URL-ul original
      return imageUrl;
    }
  } catch (error) {
    console.error('Eroare la conversia imaginii WebP:', error);
    return imageUrl; // Returnăm URL-ul original în caz de eroare
  }
};

const getImageUrl = (url) => {
  if (!url) return null;
  return processImageUrl(url);
};

// Componenta PDF pentru cererea de la furnizor
const FurnizorPDF = ({ furnizorData, groupedProducts, productRequestStatuses, calculateRequestTotal, convertedImages }) => {
  const currentDate = new Date().toLocaleDateString('ro-RO')

  // Calculează totalul general pentru toate cererile
  const calculateGrandTotal = () => {
    return Object.values(groupedProducts).reduce((grandTotal, products) => {
      return grandTotal + calculateRequestTotal(products);
    }, 0);
  };

  // Găsește statusul pentru un produs
  const getProductStatus = (statusId) => {
    return productRequestStatuses.find(status => status.id === statusId);
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.companyInfo}>
            <Text style={styles.companyName}>Danco SRL</Text>
            <Text style={styles.companyDetails}>Chișinău, Moldova</Text>
            <Text style={styles.companyDetails}>contact@danco.md</Text>
            <Text style={styles.companyDetails}>Telefon: +373 60 123 456</Text>
          </View>
          
          <View style={styles.documentTitle}>
            <Text style={styles.title}>RAPORT FURNIZOR</Text>
            <Text style={styles.subtitle}>
              Data generării: {currentDate}
            </Text>
          </View>
        </View>

        {/* Informații furnizor */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Informații furnizor</Text>
          <View style={styles.row}>
            <View style={styles.column}>
              <Text style={styles.label}>Denumire:</Text>
              <Text style={styles.value}>{furnizorData.denumire || 'N/A'}</Text>
            </View>
            <View style={styles.column}>
              <Text style={styles.label}>Brand:</Text>
              <Text style={styles.value}>{furnizorData.brand_name || 'N/A'}</Text>
            </View>
          </View>
          <View style={styles.row}>
            <View style={styles.column}>
              <Text style={styles.label}>Cod fiscal:</Text>
              <Text style={styles.value}>{furnizorData.id_no || 'N/A'}</Text>
            </View>
            <View style={styles.column}>
              <Text style={styles.label}>Termen plată:</Text>
              <Text style={styles.value}>{furnizorData.termen_de_plata} zile</Text>
            </View>
          </View>
          <View style={styles.row}>
            <View style={styles.column}>
              <Text style={styles.label}>Termen livrare:</Text>
              <Text style={styles.value}>{furnizorData.termen_de_livrare} zile</Text>
            </View>
            {furnizorData.contact && furnizorData.contact[0] && (
              <View style={styles.column}>
                <Text style={styles.label}>Contact:</Text>
                <Text style={styles.value}>{furnizorData.contact[0].email}</Text>
                <Text style={styles.value}>{furnizorData.contact[0].telefon}</Text>
              </View>
            )}
          </View>
          {furnizorData.adresa_juridica && (
            <View style={styles.row}>
              <View style={[styles.column, { width: '100%' }]}>
                <Text style={styles.label}>Adresă juridică:</Text>
                <Text style={styles.value}>{furnizorData.adresa_juridica}</Text>
              </View>
            </View>
          )}
        </View>

        {/* Sumar general */}
        <View style={styles.summary}>
          <View style={styles.summaryRow}>
            <Text>Număr cereri:</Text>
            <Text>{Object.keys(groupedProducts).length}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text>Total produse:</Text>
            <Text>{Object.values(groupedProducts).reduce((total, products) => total + products.length, 0)}</Text>
          </View>
          <View style={[styles.summaryRow, styles.summaryTotal]}>
            <Text>TOTAL GENERAL:</Text>
            <Text>{calculateGrandTotal().toFixed(2)} MDL</Text>
          </View>
        </View>

        {/* Cereri de achiziție */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Cereri de achiziție</Text>
          
          {Object.entries(groupedProducts).map(([requestId, products]) => {
            const total = calculateRequestTotal(products);
            
            return (
              <View key={requestId} style={styles.requestSection}>
                <Text style={styles.requestTitle}>Cererea #{requestId}</Text>
                
                {/* Sumar cerere */}
                <View style={styles.row}>
                  <View style={styles.column}>
                    <Text style={styles.label}>Număr produse:</Text>
                    <Text style={styles.value}>{products.length}</Text>
                  </View>
                  <View style={styles.column}>
                    <Text style={styles.label}>Total cerere:</Text>
                    <Text style={styles.value}>{total.toFixed(2)} MDL</Text>
                  </View>
                </View>

                {/* Tabel produse pentru această cerere */}
                <View style={styles.table}>
                  {/* Header tabel */}
                  <View style={[styles.tableRow, styles.tableHeader]}>
                    <Text style={[styles.tableCell, { flex: 0.5 }]}>Nr.</Text>
                    <Text style={[styles.tableCell, { flex: 3 }]}>Produs</Text>
                    <Text style={[styles.tableCell, { flex: 1 }]}>Cantitate</Text>
                  </View>

                  {/* Rânduri produse */}
                  {products.map((product, index) => {
                    const originalImageUrl = getImageUrl(product.produs.poza_principala_url);
                    const processedImageUrl = convertedImages && convertedImages[originalImageUrl] 
                      ? convertedImages[originalImageUrl] 
                      : originalImageUrl;
                    
                    return (
                      <View key={product.id} style={styles.tableRow}>
                        <Text style={[styles.tableCell, { flex: 0.5 }]}>{index + 1}</Text>
                        
                        <View style={[styles.tableCell, { flex: 3, flexDirection: 'row', alignItems: 'center' }]}>
                          {processedImageUrl && (
                            <Image 
                              src={processedImageUrl}
                              style={styles.productImage}
                              cache={false}
                            />
                          )}
                          <View style={styles.productInfo}>
                            <Text style={styles.productName}>
                              {product.produs.denumire || 'N/A'}
                            </Text>
                            {product.produs.sku_intern && (
                              <Text style={styles.productSku}>
                                SKU: {product.produs.sku_intern}
                              </Text>
                            )}
                          </View>
                        </View>
                        
                        <Text style={[styles.tableCell, { flex: 1 }]}>
                          {product.produs_oferta.product_qt} {product.produs.unit_of_measure}
                        </Text>
                      </View>
                    );
                  })}
                </View>
              </View>
            );
          })}
        </View>

        {/* Footer */}
        <View fixed style={styles.footer}>
          <Text>Raport generat pe: {currentDate} la {new Date().toLocaleTimeString('ro-RO')}</Text>
        </View>
      </Page>
    </Document>
  )
}

const FurnizorPDFGenerator = ({ 
  furnizorData, 
  groupedProducts, 
  productRequestStatuses, 
  calculateRequestTotal, 
  furnizor_id,
  isSingleRequest = false,
  requestId = null
}) => {
  const [modalVisible, setModalVisible] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [selectedRequests, setSelectedRequests] = useState([])
  const [selectAll, setSelectAll] = useState(true)
  const { token, user } = useStateContext();

  // Verifică dacă utilizatorul are permisiunea să acceseze PDF-ul (nu role_id 5)
  const canAccessPDF = user?.role_id !== 5;

  // Funcție pentru a converti WebP-urile în PNG-uri
  const convertWebPImages = async (imageUrls) => {
    const converted = {};
    for (const url of imageUrls) {
      if (url) {
        const processedUrl = await convertWebPToSupportedFormat(url);
        if (processedUrl) {
          converted[url] = processedUrl;
        }
      }
    }
    return converted;
  };

  // Inițializează selectedRequests cu toate cererile sau doar cu cererea specifică
  useEffect(() => {
    if (groupedProducts) {
      if (isSingleRequest && requestId) {
        setSelectedRequests([requestId])
        setSelectAll(true)
      } else {
        setSelectedRequests(Object.keys(groupedProducts))
      }
    }
  }, [groupedProducts, isSingleRequest, requestId])

  // Detectăm dacă utilizatorul folosește un dispozitiv mobil
  useEffect(() => {
    const checkIfMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)
      setIsMobile(isMobileDevice)
    }
    
    checkIfMobile()
    window.addEventListener('resize', checkIfMobile)
    
    return () => {
      window.removeEventListener('resize', checkIfMobile)
    }
  }, [])

  // Verificăm dacă datele sunt disponibile
  useEffect(() => {
    if (modalVisible && furnizorData) {
      if (isMobile) {
        generatePDF(false)
        setModalVisible(false)
      } else {
        setTimeout(() => {
          setIsReady(true)
        }, 500)
      }
    } else {
      setIsReady(false)
    }
  }, [modalVisible, furnizorData, isMobile])

  const handleOpenModal = () => {
    if (!canAccessPDF) {
      message.error('Nu aveți permisiunea să accesați această funcționalitate!')
      return
    }
    
    if (!furnizorData || Object.keys(groupedProducts).length === 0) {
      message.error('Nu se poate genera raportul în acest moment!')
      return
    }
    setModalVisible(true)
  }

  const handleCloseModal = () => {
    setModalVisible(false)
    setIsReady(false)
  }

  // Funcție pentru generarea PDF-ului pentru o cerere specifică
  const generateSingleRequestPDF = async (requestId, isPrint = false) => {
    if (!furnizorData || !groupedProducts[requestId]) {
      message.error('Nu se poate genera raportul pentru această cerere!')
      return
    }

    try {
      setIsGenerating(true)
      message.loading({ content: 'Se generează raportul pentru cererea...', key: 'singlePdfGeneration' })

      await new Promise(resolve => setTimeout(resolve, 1000));

      // Creăm un obiect cu doar cererea selectată
      const singleRequestData = {
        [requestId]: groupedProducts[requestId]
      }

      const blob = await pdf(
        <FurnizorPDF 
          furnizorData={furnizorData} 
          groupedProducts={singleRequestData} 
          productRequestStatuses={productRequestStatuses}
          calculateRequestTotal={calculateRequestTotal}
        />
      ).toBlob()
      
      if (isPrint) {
        const url = URL.createObjectURL(blob)
        const printWindow = window.open(url)
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print()
          }
        } else {
          message.warning('Vă rugăm să permiteți ferestrele pop-up pentru a putea printa raportul.')
        }
      } else {
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `Raport_Furnizor_${furnizorData.denumire}_Cererea_${requestId}_${new Date().toISOString().split('T')[0]}.pdf`
        link.click()
        URL.revokeObjectURL(url)
      }
      
      message.success({ content: 'Raportul pentru cerere a fost generat cu succes!', key: 'singlePdfGeneration' })
    } catch (error) {
      console.error('Eroare la generarea PDF-ului pentru cerere:', error)
      message.error({ content: 'A apărut o eroare la generarea raportului!', key: 'singlePdfGeneration' })
    } finally {
      setIsGenerating(false)
    }
  }

  // Calculează totalul general pentru cererile selectate
  const calculateGrandTotal = () => {
    return Object.entries(groupedProducts)
      .filter(([requestId]) => selectedRequests.includes(requestId))
      .reduce((grandTotal, [, products]) => {
        return grandTotal + calculateRequestTotal(products);
      }, 0);
  };

  // Găsește statusul pentru un produs
  const getProductStatus = (statusId) => {
    return productRequestStatuses.find(status => status.id === statusId);
  };

  // Funcție pentru a genera și descărca PDF-ul pentru cererile selectate
  const generatePDF = async (isPrint = false) => {
    if (!furnizorData || selectedRequests.length === 0) {
      message.error('Nu se poate genera raportul! Selectați cel puțin o cerere.')
      return
    }

    try {
      setIsGenerating(true)
      message.loading({ content: 'Se generează raportul...', key: 'pdfGeneration' })

      await new Promise(resolve => setTimeout(resolve, 1000));

      // Filtrăm doar cererile selectate
      const filteredGroupedProducts = Object.fromEntries(
        Object.entries(groupedProducts).filter(([requestId]) => selectedRequests.includes(requestId))
      )

      const imageUrlsToConvert = Object.values(filteredGroupedProducts).flatMap(products => 
        products.map(product => product.produs.poza_principala_url)
      );

      const convertedImages = await convertWebPImages(imageUrlsToConvert);

      const blob = await pdf(
        <FurnizorPDF 
          furnizorData={furnizorData} 
          groupedProducts={filteredGroupedProducts} 
          productRequestStatuses={productRequestStatuses}
          calculateRequestTotal={calculateRequestTotal}
          convertedImages={convertedImages}
        />
      ).toBlob()
      
      if (isPrint) {
        const url = URL.createObjectURL(blob)
        const printWindow = window.open(url)
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print()
          }
        } else {
          message.warning('Vă rugăm să permiteți ferestrele pop-up pentru a putea printa raportul.')
        }
      } else {
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        const requestsText = selectedRequests.length === Object.keys(groupedProducts).length 
          ? 'Toate_Cererile' 
          : `Cereri_${selectedRequests.join('_')}`
        link.download = `Raport_Furnizor_${furnizorData.denumire}_${requestsText}_${new Date().toISOString().split('T')[0]}.pdf`
        link.click()
        URL.revokeObjectURL(url)
      }
      
      message.success({ content: 'Raportul a fost generat cu succes!', key: 'pdfGeneration' })
    } catch (error) {
      console.error('Eroare la generarea PDF-ului:', error)
      message.error({ content: 'A apărut o eroare la generarea raportului!', key: 'pdfGeneration' })
    } finally {
      setIsGenerating(false)
    }
  }

  
  // Funcțiile pentru gestionarea selecției
  const handleSelectAll = (checked) => {
    setSelectAll(checked)
    if (checked) {
      setSelectedRequests(Object.keys(groupedProducts))
    } else {
      setSelectedRequests([])
    }
  }

  const handleRequestSelect = (requestId, checked) => {
    let newSelected
    if (checked) {
      newSelected = [...selectedRequests, requestId]
    } else {
      newSelected = selectedRequests.filter(id => id !== requestId)
    }
    setSelectedRequests(newSelected)
    setSelectAll(newSelected.length === Object.keys(groupedProducts).length)
  }

  // Pentru cereri individuale, generez direct PDF-ul fără modal
  const handleDirectPDF = async () => {
    if (!canAccessPDF) {
      message.error('Nu aveți permisiunea să accesați această funcționalitate!')
      return
    }
    
    if (!furnizorData || !requestId || !groupedProducts[requestId]) {
      message.error('Nu se poate genera raportul pentru această cerere!')
      return
    }

    try {
      setIsGenerating(true)
      message.loading({ content: 'Se generează raportul pentru cererea...', key: 'directPdfGeneration' })

      await new Promise(resolve => setTimeout(resolve, 1000));

      const imageUrlsToConvert = groupedProducts[requestId].map(product => product.produs.poza_principala_url);
      const convertedImages = await convertWebPImages(imageUrlsToConvert);

      const blob = await pdf(
        <FurnizorPDF 
          furnizorData={furnizorData} 
          groupedProducts={groupedProducts} 
          productRequestStatuses={productRequestStatuses}
          calculateRequestTotal={calculateRequestTotal}
          convertedImages={convertedImages}
        />
      ).toBlob()
      
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `Raport_Furnizor_${furnizorData.denumire}_Cererea_${requestId}_${new Date().toISOString().split('T')[0]}.pdf`
      link.click()
      URL.revokeObjectURL(url)
      
      message.success({ content: 'Raportul pentru cerere a fost generat cu succes!', key: 'directPdfGeneration' })
    } catch (error) {
      console.error('Eroare la generarea PDF-ului pentru cerere:', error)
      message.error({ content: 'A apărut o eroare la generarea raportului!', key: 'directPdfGeneration' })
    } finally {
      setIsGenerating(false)
    }
  }

  // Nu afișa butonul pentru role_id 5
  if (!canAccessPDF) {
    return null;
  }

  const currentDate = new Date().toLocaleDateString('ro-RO')

  // Pentru cereri individuale, afișează un buton mic
  if (isSingleRequest) {
    return (
      <Button
        size="small"
        icon={<FileTextOutlined />}
        onClick={handleDirectPDF}
        loading={isGenerating}
        disabled={isGenerating}
        title={`Generează PDF pentru cererea #${requestId}`}
        type="text"
        style={{ 
          color: '#52c41a',
          borderColor: '#52c41a'
        }}
      >
        PDF
      </Button>
    );
  }

  return (
    <>
      {/* Butonul principal */}
      <Button
        type="primary"
        icon={<EyeOutlined />}
        onClick={handleOpenModal}
        size="middle"
      >
        Generează raport PDF
      </Button>

      {/* Modal cu previzualizarea */}
      <Modal
        title={
          <div>
            Previzualizare raport furnizor
            <div style={{ fontSize: '12px', fontWeight: 'normal', color: '#666', marginTop: '4px' }}>
              {selectedRequests.length} din {Object.keys(groupedProducts).length} cereri selectate
            </div>
          </div>
        }
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={
          // Mobile: Butoane simple
          isMobile ? [
            <Button 
              key="download" 
              icon={<FilePdfOutlined />} 
              onClick={() => generatePDF(false)}
              disabled={!isReady || isGenerating || isSaving || selectedRequests.length === 0}
              loading={isGenerating}
              type="primary"
              block
            >
              Descarcă PDF
            </Button>,
            <Button key="close" onClick={handleCloseModal} block>
              Închide
            </Button>
          ] : [
            // Desktop: Toate butoanele
            <Button 
              key="download" 
              icon={<FilePdfOutlined />} 
              onClick={() => generatePDF(false)}
              disabled={!isReady || isGenerating || isSaving || selectedRequests.length === 0}
              loading={isGenerating}
            >
              Descarcă PDF
            </Button>,
            <Button 
              key="print" 
              icon={<PrinterOutlined />} 
              onClick={() => generatePDF(true)}
              disabled={!isReady || isGenerating || isSaving || selectedRequests.length === 0}
              loading={isGenerating}
            >
              Printează
            </Button>,
            <Button key="close" onClick={handleCloseModal}>
              Închide
            </Button>
          ]
        }
        width={isMobile ? '100%' : 1200}
        style={isMobile ? { top: 0, paddingBottom: 0, maxWidth: '100vw' } : { top: 20 }}
        bodyStyle={isMobile ? { padding: '12px', maxHeight: '80vh', overflow: 'auto' } : {}}
        className={isMobile ? 'mobile-modal' : ''}
      >
        <div className={isMobile ? "p-2" : "p-4"}>
          {isReady ? (
            <div>
              {/* Secțiunea de selecție cereri - doar pentru modul multi-cereri */}
              {!isSingleRequest && (
                <div className={`mb-4 p-3 bg-blue-50 rounded border ${isMobile ? 'text-sm' : ''}`}>
                  <h3 className={`font-semibold mb-3 ${isMobile ? 'text-base' : 'text-lg'}`}>
                    Selectează cererile pentru raport
                  </h3>
                  <div className="mb-3">
                    <Checkbox 
                      checked={selectAll}
                      indeterminate={selectedRequests.length > 0 && selectedRequests.length < Object.keys(groupedProducts).length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    >
                      <strong>Selectează toate cererile</strong>
                    </Checkbox>
                  </div>
                  <Divider style={{ margin: '12px 0' }} />
                  <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                    {Object.entries(groupedProducts).map(([requestId, products]) => {
                      const total = calculateRequestTotal(products);
                      return (
                        <div key={requestId} className={`${isMobile ? 'flex-col space-y-2' : 'flex justify-between items-center'} p-3 bg-white rounded border`}>
                          <Checkbox
                            checked={selectedRequests.includes(requestId)}
                            onChange={(e) => handleRequestSelect(requestId, e.target.checked)}
                          >
                            <div>
                              <div className="font-medium">Cererea #{requestId}</div>
                              <div className="text-sm text-gray-500">
                                {products.length} produse • {total.toFixed(2)} MDL
                              </div>
                            </div>
                          </Checkbox>
                          {!isMobile && (
                            <Button
                              size="small"
                              icon={<FileTextOutlined />}
                              onClick={() => generateSingleRequestPDF(requestId, false)}
                              disabled={isGenerating}
                              title="Generează PDF doar pentru această cerere"
                            >
                              PDF individual
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Butoane PDF individual pe mobile */}
                  {isMobile && (
                    <div className="mt-3">
                      <Space wrap>
                        {Object.entries(groupedProducts).map(([requestId, products]) => (
                          <Button
                            key={requestId}
                            size="small"
                            icon={<FileTextOutlined />}
                            onClick={() => generateSingleRequestPDF(requestId, false)}
                            disabled={isGenerating}
                          >
                            PDF #{requestId}
                          </Button>
                        ))}
                      </Space>
                    </div>
                  )}
                </div>
              )}

              <div className={`bg-white p-4 border rounded shadow-sm ${isMobile ? 'max-h-[50vh]' : 'max-h-[60vh]'} overflow-y-auto`}>
                {/* Previzualizare raport */}
                <div className={`${isMobile ? 'text-center mb-4' : 'flex justify-between items-center mb-8'}`}>
                  <div className={isMobile ? 'mb-4' : ''}>
                    <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                      <div className="font-medium">Danco SRL</div>
                      <div>Chișinău, Moldova</div>
                      <div>contact@danco.md</div>
                      <div>Telefon: +373 60 123 456</div>
                    </div>
                  </div>
                  <div className={isMobile ? '' : 'text-right'}>
                    <h1 className={`font-bold text-gray-800 ${isMobile ? 'text-lg' : 'text-2xl'}`}>
                      RAPORT FURNIZOR{isSingleRequest ? ` - Cererea #${requestId}` : ''}
                    </h1>
                    <div className="text-gray-600">
                      <div>Data generării: {currentDate}</div>
                    </div>
                  </div>
                </div>

                {/* Informații furnizor */}
                <div className="mb-4 p-3 bg-gray-50 rounded">
                  <h2 className={`font-semibold mb-3 ${isMobile ? 'text-base' : 'text-lg'}`}>
                    Informații furnizor
                  </h2>
                  <div className={`grid gap-2 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                    <div>
                      <div className="text-gray-600 text-sm">Denumire:</div>
                      <div className="font-medium">{furnizorData.denumire || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-gray-600 text-sm">Brand:</div>
                      <div className="font-medium">{furnizorData.brand_name || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-gray-600 text-sm">Cod fiscal:</div>
                      <div className="font-medium">{furnizorData.id_no || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-gray-600 text-sm">Termen plată:</div>
                      <div className="font-medium">{furnizorData.termen_de_plata} zile</div>
                    </div>
                    {!isMobile && (
                      <>
                        <div>
                          <div className="text-gray-600 text-sm">Termen livrare:</div>
                          <div className="font-medium">{furnizorData.termen_de_livrare} zile</div>
                        </div>
                        {furnizorData.contact && furnizorData.contact[0] && (
                          <div>
                            <div className="text-gray-600 text-sm">Contact:</div>
                            <div className="font-medium">{furnizorData.contact[0].email}</div>
                            <div className="font-medium">{furnizorData.contact[0].telefon}</div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {!isMobile && furnizorData.adresa_juridica && (
                    <div className="mt-4">
                      <div className="text-gray-600 text-sm">Adresă juridică:</div>
                      <div className="font-medium">{furnizorData.adresa_juridica}</div>
                    </div>
                  )}
                </div>

                {/* Sumar general pentru cererile selectate */}
                <div className={`${isMobile ? 'mb-4' : 'flex justify-end mb-6'}`}>
                  <div className={isMobile ? 'w-full' : 'w-64'}>
                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex justify-between py-2">
                        <div className="font-medium">
                          {isSingleRequest ? 'Cerere:' : 'Cereri selectate:'}
                        </div>
                        <div>{selectedRequests.length}</div>
                      </div>
                      <div className="flex justify-between py-2">
                        <div className="font-medium">Total produse:</div>
                        <div>
                          {Object.entries(groupedProducts)
                            .filter(([requestId]) => selectedRequests.includes(requestId))
                            .reduce((total, [, products]) => total + products.length, 0)}
                        </div>
                      </div>
                      <div className={`flex justify-between py-2 border-t border-gray-200 font-bold ${isMobile ? 'text-base' : 'text-lg'}`}>
                        <div>TOTAL{isSingleRequest ? '' : ' SELECTAT'}:</div>
                        <div>{calculateGrandTotal().toFixed(2)} MDL</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cereri de achiziție selectate - simplificat pe mobile */}
                {!isMobile && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-4">
                      {isSingleRequest 
                        ? `Cererea de achiziție #${requestId}` 
                        : `Cereri de achiziție selectate (${selectedRequests.length})`
                      }
                    </h3>
                    
                    {Object.entries(groupedProducts)
                      .filter(([requestId]) => selectedRequests.includes(requestId))
                      .map(([requestId, products]) => {
                      const total = calculateRequestTotal(products);
                      
                      return (
                        <div key={requestId} className="mb-8 border-b pb-6">
                          {!isSingleRequest && (
                            <h4 className="text-lg font-semibold text-blue-600 mb-3">Cererea #{requestId}</h4>
                          )}
                          
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <span className="text-gray-600 text-sm">Număr produse: </span>
                              <span className="font-medium">{products.length}</span>
                            </div>
                            <div>
                              <span className="text-gray-600 text-sm">Total cerere: </span>
                              <span className="font-medium">{total.toFixed(2)} MDL</span>
                            </div>
                          </div>

                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse text-sm">
                              <thead>
                                <tr className="bg-gray-100">
                                  <th className="border border-gray-300 p-2 text-left">Nr.</th>
                                  <th className="border border-gray-300 p-2 text-left">Produs</th>
                                  <th className="border border-gray-300 p-2 text-center">Cantitate</th>
                                </tr>
                              </thead>
                              <tbody>
                                {products.map((product, index) => {
                                  const productTotal = product.produs_oferta.bought_price * product.produs_oferta.product_qt;
                                  const status = getProductStatus(product.status_id);
                                  
                                  return (
                                    <tr key={product.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                      <td className="border border-gray-300 p-2">{index + 1}</td>
                                      <td className="border border-gray-300 p-2">
                                        <div className="flex items-center space-x-2">
                                          {product.produs.poza_principala_url && (
                                            <img 
                                              src={getImageUrl(product.produs.poza_principala_url)}
                                              alt={product.produs.denumire}
                                              className="w-12 h-12 object-contain rounded"
                                              onError={(e) => {
                                                e.target.style.display = 'none'
                                              }}
                                            />
                                          )}
                                          <div className="flex-1">
                                            <div className="font-medium text-sm">
                                              {product.produs.denumire || 'N/A'}
                                            </div>
                                            {product.produs.sku_intern && (
                                              <div className="text-xs text-gray-500">
                                                SKU: {product.produs.sku_intern}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </td>
                                      <td className="border border-gray-300 p-2 text-center">
                                        {product.produs_oferta.product_qt} {product.produs.unit_of_measure}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Pe mobile: doar sumar simplu */}
                {isMobile && (
                  <div className="mb-4">
                    <h3 className="text-base font-semibold mb-3">
                      Sumar cereri selectate
                    </h3>
                    {Object.entries(groupedProducts)
                      .filter(([requestId]) => selectedRequests.includes(requestId))
                      .map(([requestId, products]) => {
                      const total = calculateRequestTotal(products);
                      
                      return (
                        <div key={requestId} className="mb-3 p-3 bg-gray-50 rounded">
                          <div className="font-medium text-blue-600">Cererea #{requestId}</div>
                          <div className="text-sm text-gray-600">
                            {products.length} produse • {total.toFixed(2)} MDL
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Footer */}
                <div className="text-center text-sm text-gray-500 mt-6 pt-4 border-t border-gray-200">
                  <p>Raport generat pe: {currentDate} la {new Date().toLocaleTimeString('ro-RO')}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex justify-center items-center h-64">
              <Spin size="large" tip="Se pregătește raportul..." />
            </div>
          )}
        </div>
      </Modal>
    </>
  )
}

export default FurnizorPDFGenerator 